// ═══════════════════════════════════════════════════════════════════════════
// tokens.js — the all4indoor design system, in one file.
//
// 2026-08-06 (Jake): "it's an impressive site at the moment but I really think
// we can make it a more premium finish".
//
// He's right, and the cause is measurable. Counted in All4Indoor.js before this
// file existed:
//
//     128 distinct hex colours across 1,332 usages
//      17 distinct border-radius values (2,3,4,5,6,7,8,9,10,11,12,14,16,18,20,22,999)
//      41 distinct font sizes
//     109 linear-gradients
//       5 different reds, all meaning "accent"
//       2 different greys, both meaning "secondary text" (205 + 83 uses)
//
// Nothing on the site looks bad. It just isn't ONE system — fifteen sections
// each designed on their own night. That is exactly what "impressive but not
// quite premium" feels like from the outside.
//
// This file is the system. It renders nothing on its own; sections adopt it as
// they are converted, so the conversion can be staged and reverted per section.
// ═══════════════════════════════════════════════════════════════════════════

// ── Colour ────────────────────────────────────────────────────────────────
// Fourteen. Every one has a job, and no two share one.
export const C = {
  // Surfaces — a ramp, not a collection. Cards sit ON the page; panels sit ON
  // cards. Depth comes from the ramp, not from a gradient.
  bg:      '#08090c',
  surface: '#0e1015',
  raised:  '#14171d',
  line:    'rgba(255,255,255,0.07)',
  lineStrong: 'rgba(255,255,255,0.14)',

  // Ink — three steps. If a fourth is ever needed, the hierarchy is wrong.
  ink:   '#f2f3f5',
  ink2:  '#a7adb8',
  ink3:  '#6a707c',

  // Brand. ONE red. The old #ff6a5f and #ff5a45 were the same idea at
  // different lightness and both collapse here; accentDim is for the rare case
  // that genuinely needs a quieter brand tint.
  accent:    '#ff2e17',
  accentDim: '#ff6a5f',

  // Semantic — deliberately NOT the brand red. #e74c3c across the old file
  // means "losing", and folding it into the accent would have destroyed the
  // meaning of every form bar and verdict on the site.
  pos: '#3ecf8e',
  neg: '#e74c3c',
  warn: '#fbbf24',
};

// ── Podium ────────────────────────────────────────────────────────────────
// 2026-08-06 (Jake): "Gold Silver Bronze rest faded?"
//
// Better than what I proposed, which was neutral-with-the-accent-on-first. The
// old code cycled five arbitrary gradients — blue, rose, green, amber, purple —
// by index, so the colour said nothing at all: 4th place was amber because it
// was 4th in an array. A podium says something the moment you look at it.
export const PODIUM = [
  { bg: '#d4a72c', fg: '#231a05', label: 'Gold'   },   // 1
  { bg: '#a8b0bd', fg: '#14171d', label: 'Silver' },   // 2
  { bg: '#b06f3c', fg: '#1a0f06', label: 'Bronze' },   // 3
];
// Everyone else. Faded, so the top three read instantly without the rest
// looking broken or disabled.
export const PODIUM_REST = { bg: '#14171d', fg: '#a7adb8', label: null };

/** Podium styling for a 1-based rank. */
export function podium(rank) {
  return PODIUM[(rank || 0) - 1] || PODIUM_REST;
}

// ── Radius ────────────────────────────────────────────────────────────────
// Three, plus a pill. Seventeen values is what makes a page feel assembled
// rather than designed.
export const R = { sm: 6, md: 10, lg: 14, pill: 999 };

// ── Type ──────────────────────────────────────────────────────────────────
// Eight steps. Archivo for anything that carries weight, Inter for prose.
export const F = {
  display: "'Archivo', sans-serif",
  body: "'Inter', system-ui, -apple-system, sans-serif",
};
export const T = {
  micro: '0.62rem',   // uppercase labels under a figure
  tiny:  '0.72rem',   // secondary line in a row
  small: '0.82rem',
  base:  '0.94rem',
  lead:  '1.02rem',   // panel titles, hero paragraph
  h3:    '1.2rem',
  h2:    '2rem',
  h1:    'clamp(2.6rem, 5.4vw, 4.6rem)',
};

// ── Numerals ──────────────────────────────────────────────────────────────
// The single highest-value line in this file for a stats site, and it costs
// nothing. Proportional digits make "1" narrower than "8", so a column of
// ratings visibly shimmers out of alignment as you read down it. Spread onto
// anything that renders a figure in a column: ranks, ratings, scores, counts.
export const NUM = {
  fontVariantNumeric: 'tabular-nums',
  fontFeatureSettings: '"tnum" 1',
};

// ── Motion ────────────────────────────────────────────────────────────────
// One curve, two durations. Premium reads as restrained and consistent, never
// as more animation.
export const M = {
  fast: '0.14s cubic-bezier(0.4, 0, 0.2, 1)',
  base: '0.22s cubic-bezier(0.4, 0, 0.2, 1)',
};

// ── Elevation ─────────────────────────────────────────────────────────────
// Two. The old file had thirty box-shadows including several coloured glows
// under buttons, which is the most reliable "not premium" tell there is.
export const E = {
  card: '0 1px 2px rgba(0,0,0,0.4), 0 4px 16px rgba(0,0,0,0.25)',
  modal: '0 24px 70px rgba(0,0,0,0.55)',
};
